import React, { useEffect, useRef } from "react";

// 5 lb increments from -20 to +20 — applies uniformly to every strength
// exercise (per user direction "let's just do the 5 lb jumps").
export const DELTA_STOPS = [-20, -15, -10, -5, 0, 5, 10, 15, 20];

export const formatDelta = (v) => {
  if (v === 0) return "Same";
  const sign = v > 0 ? "+" : "−";
  return `${sign}${Math.abs(v)} lb`;
};

// ─── iOS-style wheel picker ───
// Vertical scroll-snap list with the centered row highlighted (like the
// alarm/timer wheels in iOS). Center row = current value. Tap a row to snap
// to it; native scroll/wheel/touch gestures also work and snap automatically.
const ITEM_H = 40;
const VISIBLE_ROWS = 5; // 2 above center, center, 2 below

export const WheelPicker = ({ stops = DELTA_STOPS, value, onChange, testIdPrefix = "wheel" }) => {
  const ref = useRef(null);
  const scrollEndTimer = useRef(null);
  const PAD = ((VISIBLE_ROWS - 1) / 2) * ITEM_H;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const idx = stops.indexOf(value);
    if (idx < 0) return;
    el.scrollTo({ top: idx * ITEM_H, behavior: "auto" });
  }, [value, stops]);

  const handleScroll = () => {
    clearTimeout(scrollEndTimer.current);
    scrollEndTimer.current = setTimeout(() => {
      const el = ref.current;
      if (!el) return;
      const idx = Math.round(el.scrollTop / ITEM_H);
      const clamped = Math.max(0, Math.min(stops.length - 1, idx));
      const newVal = stops[clamped];
      if (newVal !== value) onChange(newVal);
    }, 110);
  };

  const tapRow = (v) => {
    const el = ref.current;
    if (!el) return;
    const idx = stops.indexOf(v);
    if (idx < 0) return;
    el.scrollTo({ top: idx * ITEM_H, behavior: "smooth" });
  };

  return (
    <div className="relative bg-[#0B0F1A] rounded-xl overflow-hidden border border-[#1E293B]"
         style={{ height: ITEM_H * VISIBLE_ROWS }}
         data-testid={`${testIdPrefix}-wheel`}>
      <div className="absolute inset-x-0 pointer-events-none border-y-2 border-primary/50 bg-primary/[0.07]"
           style={{ top: PAD, height: ITEM_H }} />
      <div className="absolute inset-x-0 top-0 pointer-events-none z-10"
           style={{ height: PAD,
                    background: "linear-gradient(to bottom, #0B0F1A 0%, rgba(11,15,26,0.6) 50%, transparent 100%)" }} />
      <div className="absolute inset-x-0 bottom-0 pointer-events-none z-10"
           style={{ height: PAD,
                    background: "linear-gradient(to top, #0B0F1A 0%, rgba(11,15,26,0.6) 50%, transparent 100%)" }} />
      <div
        ref={ref}
        onScroll={handleScroll}
        className="h-full overflow-y-scroll wheel-scroll"
        style={{
          scrollSnapType: "y mandatory",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          paddingTop: PAD,
          paddingBottom: PAD,
          WebkitOverflowScrolling: "touch",
        }}>
        <style>{`.wheel-scroll::-webkit-scrollbar{display:none}`}</style>
        {stops.map(s => {
          const active = s === value;
          return (
            <div
              key={s}
              onClick={() => tapRow(s)}
              className="flex items-center justify-center cursor-pointer select-none transition-all"
              style={{
                height: ITEM_H,
                scrollSnapAlign: "start",
                color: active ? "#fff" : "rgba(255,255,255,0.32)",
                fontSize: active ? 22 : 16,
                fontWeight: active ? 800 : 500,
                letterSpacing: active ? "0.02em" : "0",
              }}
              data-testid={`${testIdPrefix}-row-${s}`}>
              {formatDelta(s)}
            </div>
          );
        })}
      </div>
    </div>
  );
};
